import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import {
  Target, Inbox as InboxIcon, FolderOpen, FileText, ArrowRight,
  Mail, MessageCircle, Globe, User, Clock, AlertCircle, TrendingUp, Info, Plus, X,
  ClipboardList, ClipboardCheck, Stethoscope, Activity,
} from "lucide-react";

const STATUS_META = {
  priority_needed: { label: "Priority needed", color: "#ef4444" },
  evaluating:      { label: "Evaluating",      color: "#f59e0b" },
  covered:         { label: "Covered",         color: "#10b981" },
  not_a_priority:  { label: "Not a priority",  color: "#64748b" },
};

const CAND_TONE = {
  active_target: "pm-pill-blue",
  offer_made:    "pm-pill-amber",
  deal_done:     "pm-pill-green",
  rejected:      "pm-pill-red",
  pending:       "pm-pill-slate",
};

const SOURCE_ICON = { email: Mail, whatsapp: MessageCircle, pdf: FileText, transfermarkt: Globe, manual: User };

export default function TechDirectorHome() {
  const [data, setData] = useState(null);
  const [crossSuite, setCrossSuite] = useState(null);
  const [addTargetOpen, setAddTargetOpen] = useState(false);

  const load = () => Promise.all([
    api.get("/td/dashboard").then((r) => setData(r.data)),
    api.get("/td/cross-suite").then((r) => setCrossSuite(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <Layout title="Club home" subtitle="Tech director · transfer window operations">
        <div className="text-slate-500" data-testid="td-loading">Loading club operations…</div>
      </Layout>
    );
  }

  const { window: win, slot_health, candidates_by_status, candidates_count, budget, intakes, top_targets, inbox_threads, scouting_docs } = data;

  if (!win) {
    return (
      <Layout title="Club home" subtitle="Tech director · transfer window operations">
        <div className="pm-card p-8 text-center" data-testid="td-no-window">
          <Target className="mx-auto mb-3 text-slate-400" size={28} />
          <div className="font-display text-lg font-black tracking-tight">No active transfer window</div>
          <div className="text-sm text-slate-500 mt-1">Create one from the Position map to start tracking targets.</div>
          <Link to="/position-map" className="pm-btn-primary inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-sm font-bold mt-4">
            Open Position map <ArrowRight size={13} />
          </Link>
        </div>
      </Layout>
    );
  }

  const total = budget?.total || 0;
  const committed = budget?.committed || 0;
  const explored = budget?.explored || 0;
  const remaining = budget?.remaining ?? (total - committed);
  const committedPct = total ? Math.min(100, (committed / total) * 100) : 0;
  const exploredPct = total ? Math.min(100 - committedPct, (explored / total) * 100) : 0;

  // Tiered urgency for window close date
  const dtc = win.days_to_close;
  const urgencyTier = dtc == null ? "ok" : dtc <= 7 ? "red" : dtc <= 30 ? "amber" : "ok";
  const urgencyTone =
    urgencyTier === "red"   ? "text-red-600"
    : urgencyTier === "amber" ? "text-amber-600"
    : "text-slate-900";
  const urgencyChip =
    urgencyTier === "red"   ? "pm-pill-red"
    : urgencyTier === "amber" ? "pm-pill-amber"
    : "pm-pill-slate";
  const urgencyLabel =
    urgencyTier === "red"   ? "DEADLINE WEEK"
    : urgencyTier === "amber" ? "LESS THAN A MONTH"
    : "ON TRACK";

  return (
    <Layout
      title="Club home"
      subtitle={`${win.team_name} · ${win.name}`}
    >
      <div className="space-y-6" data-testid="td-home-root">
        {/* Window header */}
        <div className="pm-card p-5 flex flex-wrap items-center gap-5" data-testid="td-window-header">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Window</div>
            <div className="font-display text-2xl font-black tracking-tight">{win.name}</div>
            <div className="text-xs text-slate-500">{win.season} · Formation {win.formation}</div>
          </div>
          <div className="h-12 w-px bg-slate-200 mx-2" />
          <div data-testid="td-days-to-close">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Closes in</div>
            <div className={`font-display text-3xl font-black tracking-tight flex items-center gap-2 ${urgencyTone}`}>
              {dtc !== null ? `${dtc}d` : "—"}
              <span className={`pm-pill ${urgencyChip} text-[9px]`} data-testid="td-urgency-chip">{urgencyLabel}</span>
            </div>
            <div className="text-xs text-slate-500">{fmtDate(win.close_date)}</div>
          </div>
          <div className="h-12 w-px bg-slate-200 mx-2 hidden md:block" />
          <div data-testid="td-candidates-count">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Tracked candidates</div>
            <div className="font-display text-3xl font-black tracking-tight">{candidates_count}</div>
            <div className="text-xs text-slate-500">across {Object.values(slot_health).reduce((a, b) => a + b, 0)} slots</div>
          </div>
          <Link
            to="/position-map"
            className="ml-auto pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold flex items-center gap-2"
            data-testid="td-open-position-map"
          >
            Open Position map <ArrowRight size={14} />
          </Link>
        </div>

        {/* Row 1: slot health + budget */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Slot health */}
          <div className="pm-card p-5" data-testid="td-slot-health">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Slot health</div>
                <div className="font-display text-xl font-black tracking-tight">Where targets are needed</div>
              </div>
              <Target size={18} className="text-slate-400" />
            </div>
            <div className="space-y-2.5">
              {Object.entries(slot_health).map(([k, v]) => {
                const m = STATUS_META[k] || STATUS_META.not_a_priority;
                const totalSlots = Object.values(slot_health).reduce((a, b) => a + b, 0);
                const pct = totalSlots ? (v / totalSlots) * 100 : 0;
                return (
                  <div key={k} data-testid={`slot-bar-${k}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2 font-bold text-slate-800">
                        <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                        {m.label}
                      </span>
                      <span className="font-mono-tab text-slate-500">{v} slots</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Budget */}
          <div className="pm-card p-5" data-testid="td-budget">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Window budget</div>
                <div className="font-display text-xl font-black tracking-tight">{fmtMoney(total)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Remaining</div>
                <div className={`font-display text-2xl font-black tracking-tight ${remaining < 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {fmtMoney(remaining)}
                </div>
              </div>
            </div>
            <div
              className="h-3 rounded-full bg-slate-100 overflow-hidden flex mb-2"
              title={`Committed ${fmtMoney(committed)} · Exploring (not committed) ${fmtMoney(explored)} · Capacity ${fmtMoney(total)}`}
            >
              <div className="h-full bg-amber-500" style={{ width: `${committedPct}%` }} title={`Committed ${fmtMoney(committed)}`} />
              <div className="h-full bg-blue-400 opacity-70" style={{ width: `${exploredPct}%` }} title={`Exploring (not committed) ${fmtMoney(explored)}`} />
            </div>
            <div className="flex items-start gap-1.5 text-[11px] text-slate-500 mb-3 leading-snug" data-testid="td-budget-note">
              <Info size={11} className="text-slate-400 mt-0.5 shrink-0" />
              <span>
                <b className="text-slate-700">Remaining</b> = Capacity − Committed only. Exploring totals can legitimately exceed capacity because a target only counts against budget once an offer is made.
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Mini tone="amber" label="Committed"  value={fmtMoney(committed)} hint="offer / done" testid="td-budget-committed" />
              <Mini tone="blue"  label="Exploring"  value={fmtMoney(explored)}  hint="not yet spent" testid="td-budget-explored" />
              <Mini tone="slate" label="Capacity"   value={fmtMoney(total)}     hint="window cap"    testid="td-budget-total" />
            </div>
          </div>
        </div>

        {/* Row 2: top targets + pending intake */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top targets */}
          <div className="pm-card p-5" data-testid="td-top-targets">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Priority funnel</div>
                <div className="font-display text-xl font-black tracking-tight flex items-center gap-2"><TrendingUp size={16} /> Top targets</div>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-end">
                {Object.entries(candidates_by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} className={`pm-pill text-[10px] ${CAND_TONE[k]}`} data-testid={`cand-status-${k}`}>{k.replace("_", " ")} {v}</span>
                ))}
              </div>
            </div>
            {top_targets.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl" data-testid="td-no-targets">
                No priority targets yet — head to the Position map to add candidates to your priority slots.
              </div>
            )}
            <div className="space-y-2">
              {top_targets.map((t) => (
                <div key={t.id} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`td-target-${t.id}`}>
                  <span className="font-mono-tab font-bold text-xs text-slate-500 w-6 text-center">#{t.priority_rank}</span>
                  <div
                    className="w-10 h-10 rounded-xl bg-slate-100 shrink-0"
                    style={{ backgroundImage: t.photo_url ? `url(${t.photo_url})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate flex items-center gap-2">
                      {t.player_name}
                      <span className="pm-pill pm-pill-slate text-[10px]">{t.slot_label}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{t.position} · {t.age ? `${t.age}y` : ""} · {t.current_club}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`pm-pill text-[10px] ${CAND_TONE[t.status]}`}>{t.status.replace("_", " ")}</span>
                    <div className="font-mono-tab text-[11px] text-slate-700 mt-1">{fmtMoney(t.asking_fee_eur)}</div>
                  </div>
                </div>
              ))}
            </div>
            {candidates_count > top_targets.length && (
              <div className="mt-2 text-right">
                <Link to="/position-map" className="text-xs font-bold text-emerald-700 hover:text-emerald-800" data-testid="td-targets-view-all">
                  View all {candidates_count} candidates →
                </Link>
              </div>
            )}
          </div>

          {/* Pending intake */}
          <div className="pm-card p-5" data-testid="td-pending-intake">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Inbound from agents</div>
                <div className="font-display text-xl font-black tracking-tight flex items-center gap-2"><InboxIcon size={16} /> Pending intake ({intakes.length})</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setAddTargetOpen(true)}
                  className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5"
                  data-testid="td-add-target-btn"
                  title="Add a target manually without going through the AI intake queue"
                >
                  <Plus size={12} /> Add target
                </button>
                <Link to="/position-map" className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid="td-intake-link">
                  Manage <ArrowRight size={11} />
                </Link>
              </div>
            </div>
            {intakes.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl" data-testid="td-no-intake">
                Inbox is clear. Forward an email, WhatsApp, PDF dossier, or Transfermarkt link to your scouting aliases to populate this queue.
              </div>
            )}
            <div className="space-y-2">
              {intakes.map((i) => {
                const Icon = SOURCE_ICON[i.source] || User;
                return (
                  <div key={i.id} className="border border-slate-200 rounded-xl p-3 flex items-start gap-3" data-testid={`td-intake-${i.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm truncate flex items-center gap-1.5">
                        {i.player_name || "Unknown"}
                        <span className="pm-pill pm-pill-slate text-[10px]">{i.position || "—"}</span>
                        <span className="font-mono-tab text-[10px] text-slate-500">{Math.round((i.confidence || 0) * 100)}%</span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{i.nationality || ""} · {i.current_club || ""}</div>
                      <div className="text-[11px] font-mono-tab text-slate-700 mt-0.5">MV {fmtMoney(i.market_value_eur)} · Ask {fmtMoney(i.asking_fee_eur)}</div>
                      <div className="text-[10px] text-slate-400 truncate mt-0.5">via {i.source} · {i.sender}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 3: inbox + scouting docs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inbox preview */}
          <div className="pm-card p-5" data-testid="td-inbox">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Inbox</div>
                <div className="font-display text-xl font-black tracking-tight flex items-center gap-2"><Mail size={16} /> Club & scout threads</div>
              </div>
              <Link to="/inbox" className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid="td-inbox-link">
                Open inbox <ArrowRight size={11} />
              </Link>
            </div>
            {inbox_threads.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center" data-testid="td-no-inbox">No messages.</div>
            )}
            <div className="space-y-2">
              {inbox_threads.slice(0, 4).map((m) => (
                <div key={m.id} className="border border-slate-200 rounded-xl p-3 flex items-start gap-3" data-testid={`td-thread-${m.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 text-xs font-bold">
                    {m.sender_name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-sm truncate">{m.sender_name}</span>
                      <span className="pm-pill pm-pill-slate text-[10px]">{m.category}</span>
                      {!m.is_read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="unread" />}
                    </div>
                    <div className="text-[12px] text-slate-700 truncate font-semibold">{m.subject || "(no subject)"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{(m.body || "").slice(0, 90)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scouting docs */}
          <div className="pm-card p-5" data-testid="td-scouting-docs">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Knowledge</div>
                <div className="font-display text-xl font-black tracking-tight flex items-center gap-2"><FolderOpen size={16} /> Shared scouting docs</div>
              </div>
              <Link to="/scouting-docs" className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid="td-docs-link">
                Browse all <ArrowRight size={11} />
              </Link>
            </div>
            <div className="space-y-2">
              {scouting_docs.map((d) => (
                <div key={d.id} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`td-doc-${d.id}`}>
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-700 shrink-0">
                    <FileText size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{d.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{d.author} · {fmtDate(d.uploaded_at)} · {(d.size_kb / 1024).toFixed(1)} MB</div>
                  </div>
                  <span className="pm-pill pm-pill-slate text-[10px]">{d.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cross-suite digest */}
        {crossSuite && (
          <section data-testid="td-cross-suite">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Cross-suite</div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Link to="/scouting-home" className="pm-card p-5 hover:border-slate-400 transition-colors" data-testid="td-tile-scout-briefs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><ClipboardList size={16} className="text-amber-600" />
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Scout briefs</div>
                  </div>
                  <ArrowRight size={14} className="text-slate-400" />
                </div>
                <div className="font-display text-3xl font-black tracking-tight mt-2">{crossSuite.scout_briefs.open}<span className="text-base text-slate-400 font-normal"> / {crossSuite.scout_briefs.total}</span></div>
                <div className="text-xs text-slate-500">{crossSuite.scout_briefs.in_progress} in progress · {crossSuite.pending_submissions_count} pending decisions</div>
                <div className="mt-3 space-y-1 text-[11px] text-slate-600">
                  {crossSuite.scout_briefs.recent.slice(0, 3).map((b) => (
                    <div key={b.id} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />{b.slot_label} — {b.position}</div>
                  ))}
                </div>
              </Link>
              <Link to="/staff-home" className="pm-card p-5 hover:border-slate-400 transition-colors" data-testid="td-tile-tactical-fit">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><ClipboardCheck size={16} className="text-blue-600" />
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Tactical fit log</div>
                  </div>
                  <ArrowRight size={14} className="text-slate-400" />
                </div>
                <div className="font-display text-3xl font-black tracking-tight mt-2">{crossSuite.tactical_fits.assessed}<span className="text-base text-slate-400 font-normal"> / {crossSuite.tactical_fits.total}</span></div>
                <div className="text-xs text-slate-500">Assessed by coaching staff</div>
                <div className="mt-3 space-y-1 text-[11px] text-slate-600">
                  {crossSuite.tactical_fits.recent.slice(0, 3).map((t) => (
                    <div key={t.id} className="flex items-center gap-2 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="truncate">{t.candidate_name}</span>
                      {t.recommendation && <span className="text-emerald-700 text-[10px] font-bold truncate">— {t.recommendation.replace("_", " ")}</span>}
                    </div>
                  ))}
                </div>
              </Link>
              <Link to="/staff-home" className="pm-card p-5 hover:border-slate-400 transition-colors" data-testid="td-tile-medical">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Stethoscope size={16} className="text-emerald-600" />
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Medical summary</div>
                  </div>
                  <ArrowRight size={14} className="text-slate-400" />
                </div>
                <div className="font-display text-3xl font-black tracking-tight mt-2">{crossSuite.medicals.pending}<span className="text-base text-slate-400 font-normal"> / {crossSuite.medicals.total}</span></div>
                <div className="text-xs text-slate-500">{crossSuite.long_term_injuries.length} long-term injuries flagged</div>
                <div className="mt-3 space-y-1 text-[11px] text-slate-600">
                  {crossSuite.medicals.recent.slice(0, 2).map((m) => (
                    <div key={m.id} className="flex items-center gap-2 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="truncate">{m.candidate_name} — {m.status.replace("_", " ")}</span>
                    </div>
                  ))}
                  {crossSuite.long_term_injuries.slice(0, 1).map((i) => (
                    <div key={i.id} className="flex items-center gap-2 truncate text-red-700">
                      <Activity size={9} /> {i.player_name} — {i.diagnosis}
                    </div>
                  ))}
                </div>
              </Link>
            </div>
            <div className="text-[10px] text-slate-400 mt-2">
              Summaries only — medical confidentials, scout notes, and staff comments stay inside the suites that own them.
            </div>
          </section>
        )}

        {/* RBAC footer */}
        <div className="pm-card p-4 flex items-start gap-3 border-l-4 border-l-emerald-400 bg-emerald-50/30" data-testid="td-rbac-note">
          <AlertCircle size={14} className="text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-700">
            Your account is scoped to the <b>club side</b> of Playmaker. You see the position map, candidate pipeline, club & scout inbox threads, and shared scouting docs. Agent financials, commissions, payroll, and client homepages are not visible to your role.
          </div>
        </div>
      </div>

      {addTargetOpen && (
        <AddTargetModal
          windowId={win.id}
          slots={data.slots || []}
          onClose={() => setAddTargetOpen(false)}
          onSaved={() => { setAddTargetOpen(false); load(); }}
        />
      )}
    </Layout>
  );
}

function AddTargetModal({ windowId, slots, onClose, onSaved }) {
  const [form, setForm] = useState({
    player_name: "",
    position: "",
    age: "",
    nationality: "",
    current_club: "",
    contract_end: "",
    market_value_eur: "",
    asking_fee_eur: "",
    slot_id: "",
    priority_rank: 1,
    status: "active_target",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.player_name.trim() && form.slot_id;

  const submit = async () => {
    if (!valid) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/position-map/candidates", {
        window_id: windowId,
        slot_id: form.slot_id,
        player_name: form.player_name.trim(),
        position: form.position || "—",
        age: form.age ? Number(form.age) : null,
        nationality: form.nationality || null,
        current_club: form.current_club || null,
        contract_end: form.contract_end || null,
        market_value_eur: form.market_value_eur ? Number(form.market_value_eur) : null,
        asking_fee_eur: form.asking_fee_eur ? Number(form.asking_fee_eur) : null,
        source: "manual",
        source_contact: "TD manual entry",
        priority_rank: Number(form.priority_rank) || 1,
        status: form.status,
        notes: form.notes || null,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="add-target-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Manual entry</div>
            <h3 className="font-display text-xl font-black tracking-tight">Add a target</h3>
            <div className="text-xs text-slate-500 mt-0.5">Use this for verbal tips or self-scouted players not yet in the AI intake queue.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="add-target-close"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Player name *">
            <input className="pm-input" value={form.player_name} onChange={(e) => setF("player_name", e.target.value)} data-testid="add-target-name" placeholder="Full name" />
          </Field>
          <Field label="Position">
            <input className="pm-input" value={form.position} onChange={(e) => setF("position", e.target.value)} placeholder="e.g. CB, LW" />
          </Field>
          <Field label="Age">
            <input type="number" className="pm-input" value={form.age} onChange={(e) => setF("age", e.target.value)} />
          </Field>
          <Field label="Nationality">
            <input className="pm-input" value={form.nationality} onChange={(e) => setF("nationality", e.target.value)} />
          </Field>
          <Field label="Current club" wide>
            <input className="pm-input" value={form.current_club} onChange={(e) => setF("current_club", e.target.value)} />
          </Field>
          <Field label="Contract end">
            <input type="date" className="pm-input" value={form.contract_end} onChange={(e) => setF("contract_end", e.target.value)} />
          </Field>
          <Field label="Market value (€)">
            <input type="number" className="pm-input" value={form.market_value_eur} onChange={(e) => setF("market_value_eur", e.target.value)} />
          </Field>
          <Field label="Asking fee (€)">
            <input type="number" className="pm-input" value={form.asking_fee_eur} onChange={(e) => setF("asking_fee_eur", e.target.value)} />
          </Field>
          <Field label="Status">
            <select className="pm-input" value={form.status} onChange={(e) => setF("status", e.target.value)}>
              <option value="active_target">active target</option>
              <option value="offer_made">offer made</option>
              <option value="deal_done">deal done</option>
              <option value="rejected">rejected</option>
            </select>
          </Field>
          <Field label="Slot *" wide>
            <select
              className="pm-input"
              value={form.slot_id}
              onChange={(e) => setF("slot_id", e.target.value)}
              data-testid="add-target-slot"
            >
              <option value="">— choose slot —</option>
              {slots.map((s) => <option key={s.id} value={s.id}>{s.slot} · {s.status.replace("_", " ")}</option>)}
            </select>
          </Field>
          <Field label="Priority rank">
            <input type="number" min={1} className="pm-input" value={form.priority_rank} onChange={(e) => setF("priority_rank", e.target.value)} />
          </Field>
          <Field label="Notes" wide>
            <textarea className="pm-input" rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} />
          </Field>
        </div>

        {err && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{String(err)}</div>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" onClick={onClose}>Cancel</button>
          <button
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold disabled:opacity-50"
            disabled={!valid || busy}
            onClick={submit}
            data-testid="add-target-submit"
          >
            {busy ? "Saving…" : "Add to position map"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Mini({ tone, label, value, hint, testid }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50"
    : tone === "blue" ? "border-blue-200 bg-blue-50"
    : "border-slate-200 bg-slate-50";
  return (
    <div className={`rounded-xl border ${cls} p-2.5`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className="font-display text-sm font-black tracking-tight">{value}</div>
      <div className="text-[10px] text-slate-500">{hint}</div>
    </div>
  );
}
